import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button, IconButton } from '@mui/material';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { dataGridCol } from '@/app/shared/components/Grid/common';
import { CPGrid } from '@/app/shared/components/Grid/CPGrid';
import type { TPhoenixColumnDef } from '@/app/shared/components/Grid/utility/types';
import { downloadAsData, gridRowActionsIconProps } from '@/app/shared/utils/grid';
import { useAppSelector } from '@/store/hooks';
import { useFetchUserDocumentsQuery } from '../uploadStips/uploadStips.services';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { useFetchStipsQuery, useLazyFetchStipsDetailsQuery } from './stips.services';
import type { DocumentItem, IStip } from './stips.types';

const Stips = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [fetchStipsDetails] = useLazyFetchStipsDetailsQuery();
  const [documentPreviewModalPosition, setDocumentPreviewModalPosition] = useState(0);
  const [contactStipData, setContactStipData] = useState<DocumentItem>();
  const [stipData, setStipData] = useState<IStip | null>(null);
  const [loadingStipData, setLoadingStipData] = useState(false);
  const reduxContactId = useAppSelector((state) => state.auth.contactId);
  const contactId = searchParams.get('contactId') || reduxContactId || '';
  const { data, isLoading } = useFetchStipsQuery(contactId ?? '', {
    skip: !contactId,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: documents,
    isLoading: isFetchDocumentLoading,
    error: documentsError,
  } = useFetchUserDocumentsQuery(
    { contactId },
    { skip: !contactId }
  );

  const hasDocuments =
    documents &&
    !documentsError &&
    Array.isArray(documents?.stipsRequestItems) &&
    documents.stipsRequestItems.length > 0;

  const downloadFile = useCallback(
    async (row: DocumentItem) => {
      try {
        setLoadingStipData(true);

        const response = await fetchStipsDetails({
          contactStipId: row.contactStipId,
          contactId,
        }).unwrap();

        setStipData(response);

        if (
          response?.content
        ) {
          downloadAsData(
            response.blobFile.name,
            response.content
          );
        }
      } finally {
        setLoadingStipData(false);
      }
    },
    [fetchStipsDetails]
  );

  const onButtonClick = async (row: DocumentItem) => {
    setContactStipData(row);

    const response = await fetchStipsDetails({
      contactStipId: row.contactStipId,
      contactId,
    }).unwrap();

    setStipData(response);

    setDocumentPreviewModalPosition((prev) => prev + 1);
  };

  const columns: TPhoenixColumnDef[] = [
    dataGridCol('fileName', {
      minWidth: 350,
      flex: 2,
      renderCell: ({ value, row }) => (
        <Button
          disabled={row.isDeleted}
          onClick={() => onButtonClick(row)}
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '1.1rem',
            },
          }}
        >
          {value}
        </Button>
      ),
    }),
    dataGridCol('actions', {
      minWidth: 120,
      flex: 2,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'left' }}>
          <IconButton onClick={() => downloadFile(row)}>
            <FileDownloadIcon sx={gridRowActionsIconProps} />
          </IconButton>
        </div>
      )
    }),
  ];


  const rows =
    data?.documents?.map((doc, index) => ({
      id: doc.contactStipId || index,
      contactStipId: doc.contactStipId,
      fileName: doc.fileName,
      actions: '',
    })) ?? [];


  const handleUploadStips = () => {
    navigate(`/upload${location.search}`);
  };

  if (isFetchDocumentLoading) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {hasDocuments && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            my: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{
              fontSize: {
                xs: '0.6rem',
                sm: '1.1rem',
              },
            }}
            onClick={handleUploadStips}
          >
            Upload Documents
          </Button>
        </Box>
      )}

      <Box sx={{ height: 'auto', width: '100%', my: 2 }}>
        <CPGrid
          name="stipsGrid"
          canNotCopy
          canNotEdit
          autoHeight
          columns={columns}
          loading={isLoading}
          pageSize={10}
          paginationMode="client"
          filterMode="client"
          getRowId={(row) => row.contactStipId}
          rows={isLoading ? [] : [...rows]}
        />
      </Box>
      {contactStipData && !loadingStipData && stipData && (
        <DocumentPreviewModal
          documentPreviewModalPosition={documentPreviewModalPosition}
          open={!!documentPreviewModalPosition}
          contactStipData={contactStipData}
          stipData={stipData}
          onClose={() => {
            setDocumentPreviewModalPosition(0);
            setStipData(null);
          }}
        />
      )}
    </Box>
  );
};

export default Stips;